import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { CookieBanner } from "@/components/CookieBanner";
import { PaolaAssistantWidget } from "@/components/PaolaAssistantWidget";
import { AdminProtectedRoute } from "@/components/AdminProtectedRoute";
import { PageTrackingProvider } from "@/components/PageTrackingProvider";
import ScrollRestorationHandler from "@/components/ScrollRestoration";
import { Button } from "@/components/ui/button";
import { Component, Suspense, type ErrorInfo, type ReactNode } from "react";
import { useAdminShortcut } from "@/hooks/useAdminShortcut";
import {
  clearDynamicImportReloadMarker,
  isDynamicImportError,
  lazyWithRetry,
  reloadForFreshDynamicImports,
} from "@/lib/lazyWithRetry";

// Eagerly load critical landing page
import Index from "./pages/Index";

class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Application error:", error, errorInfo);
  }

  private reloadPage = () => {
    clearDynamicImportReloadMarker();
    reloadForFreshDynamicImports();
  };

  render() {
    if (!this.state.error) return this.props.children;

    const message = isDynamicImportError(this.state.error)
      ? "Aggiornamento in corso. Ricarichi la pagina per completare il caricamento."
      : "Si è verificato un errore temporaneo.";

    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 text-center shadow-lg">
          <h1 className="text-2xl font-semibold text-foreground mb-3">Domus Flexi</h1>
          <p className="text-sm text-muted-foreground mb-5">{message}</p>
          <Button
            type="button"
            onClick={this.reloadPage}
          >
            Ricarica pagina
          </Button>
        </div>
      </div>
    );
  }
}

// Lazy load all other pages to reduce initial bundle size
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TerminiServizio = lazyWithRetry(() => import("./pages/TerminiServizio"));
const CookiePolicy = lazyWithRetry(() => import("./pages/CookiePolicy"));
const ServiziPremium = lazyWithRetry(() => import("./pages/ServiziPremium"));
const IscrivitiOra = lazyWithRetry(() => import("./pages/IscrivitiOra"));
const LineeDiProdotto = lazyWithRetry(() => import("./pages/LineeDiProdotto"));
const ChiPerche = lazyWithRetry(() => import("./pages/ChiPerche"));
const Appuntamento = lazyWithRetry(() => import("./pages/Appuntamento"));
const Documenti = lazyWithRetry(() => import("./pages/Documenti"));
const TariffeServizi = lazyWithRetry(() => import("./pages/TariffeServizi"));
const IscrizioneProprietario = lazyWithRetry(() => import("./pages/IscrizioneProprietario"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const AdminLogin = lazyWithRetry(() => import("./pages/AdminLogin"));
const AdminSetup = lazyWithRetry(() => import("./pages/AdminSetup"));
const AdminPermissionsManager = lazyWithRetry(() => import("./pages/AdminPermissionsManager"));
const AdminGuida = lazyWithRetry(() => import("./pages/AdminGuida"));
const CercaAnnunci = lazyWithRetry(() => import("./pages/CercaAnnunci"));
const DettaglioAnnuncio = lazyWithRetry(() => import("./pages/DettaglioAnnuncio"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Profilo = lazyWithRetry(() => import("./pages/Profilo"));
const Preferiti = lazyWithRetry(() => import("./pages/Preferiti"));
const ModificaAnnuncio = lazyWithRetry(() => import("./pages/ModificaAnnuncio"));
const TestResend = lazyWithRetry(() => import("./pages/TestResend"));
const TestEmail = lazyWithRetry(() => import("./pages/TestEmail"));
const VerificaIdentita = lazyWithRetry(() => import("./pages/VerificaIdentita"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const SocialLanding = lazyWithRetry(() => import("./pages/SocialLanding"));
const ImpegnoInquilino = lazyWithRetry(() => import("./pages/ImpegnoInquilino"));
const ConsentForm = lazyWithRetry(() => import("./pages/ConsentForm"));
const EmailPreviewDemo = lazyWithRetry(() => import("./pages/EmailPreviewDemo"));
const ChatAssistente = lazyWithRetry(() => import("./pages/ChatAssistente"));
const AffittiMilano = lazyWithRetry(() => import("./pages/AffittiMilano"));
const AffittiNapoli = lazyWithRetry(() => import("./pages/AffittiNapoli"));
const AffittiRoma = lazyWithRetry(() => import("./pages/AffittiRoma"));
const AffittiItalia = lazyWithRetry(() => import("./pages/AffittiItalia"));
const ComeFunziona = lazyWithRetry(() => import("./pages/ComeFunziona"));
const ChiSiamo = lazyWithRetry(() => import("./pages/ChiSiamo"));
const SicurezzaVerifiche = lazyWithRetry(() => import("./pages/SicurezzaVerifiche"));
const CommunityStudenti = lazyWithRetry(() => import("./pages/CommunityStudenti"));
const StudentOnboarding = lazyWithRetry(() => import("./pages/StudentOnboarding"));
const StudentDashboard = lazyWithRetry(() => import("./pages/StudentDashboard"));
const DocumentiContratto = lazyWithRetry(() => import("./pages/DocumentiContratto"));
const RiceviAvvisi = lazyWithRetry(() => import("./pages/RiceviAvvisi"));
const ModuloDati = lazyWithRetry(() => import("./pages/ModuloDati"));
const RichiestaCanoneConcordato = lazyWithRetry(() => import("./pages/RichiestaCanoneConcordato"));
const CanoneConcordato = lazyWithRetry(() => import("./pages/CanoneConcordato"));
const AffittiStudentiMilano = lazyWithRetry(() => import("./pages/AffittiStudentiMilano"));
const AdsLanding = lazyWithRetry(() => import("./pages/AdsLanding"));
const MandatoUnioncasa = lazyWithRetry(() => import("./pages/MandatoUnioncasa"));
const DanteShortTerm = lazyWithRetry(() => import("./pages/DanteShortTerm"));
const IscrizioneShortTerm = lazyWithRetry(() => import("./pages/IscrizioneShortTerm"));
const Paola = lazyWithRetry(() => import("./pages/Paola"));
const AvatarDemo = lazyWithRetry(() => import("./pages/AvatarDemo"));
const LipSyncDemo = lazyWithRetry(() => import("./pages/LipSyncDemo"));
const VisitaRisposta = lazyWithRetry(() => import("./pages/VisitaRisposta"));
const SceltaOrario = lazyWithRetry(() => import("./pages/SceltaOrario"));

const queryClient = new QueryClient();

// Minimal loading fallback that doesn't cause layout shifts
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Component to enable admin keyboard shortcut
const AdminShortcutHandler = () => {
  useAdminShortcut();
  return null;
};

const FloatingAssistant = () => {
  const location = useLocation();
  const path = location.pathname;
  // Hide on Paola pages and on all back-office / non-public routes
  const hiddenExact = [
    "/paola",
    "/assistente-vocale",
    "/dashboard",
    "/profilo",
    "/preferiti",
    "/verifica-identita",
    "/test-resend",
    "/test-email",
    "/avatar-demo",
    "/lip-sync-demo",
    "/modulo-dati",
    "/consent-form",
    "/visita-risposta",
    "/scelta-orario",
  ];
  const hiddenPrefixes = [
    "/console-admin",
    "/dashboard/",
    "/modifica-annuncio",
    "/community-studenti/dashboard",
    "/community-studenti/onboarding",
    "/impegno/",
  ];
  if (hiddenExact.includes(path)) return null;
  if (hiddenPrefixes.some((p) => path === p || path.startsWith(p))) return null;
  return <PaolaAssistantWidget />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AppErrorBoundary>
        <BrowserRouter>
          <PageTrackingProvider>
            <AdminShortcutHandler />
            <ScrollRestorationHandler />
            <Suspense fallback={<PageLoader />}>
              <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/termini-servizio" element={<TerminiServizio />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/servizi-premium" element={<ServiziPremium />} />
              <Route path="/iscriviti-ora" element={<IscrivitiOra />} />
              <Route path="/linee-di-prodotto" element={<LineeDiProdotto />} />
              <Route path="/chi-perche" element={<ChiPerche />} />
              <Route path="/appuntamento" element={<Appuntamento />} />
              <Route path="/documenti" element={<Documenti />} />
              <Route path="/tariffe-servizi" element={<TariffeServizi />} />
              <Route path="/iscrizione-proprietario" element={<IscrizioneProprietario />} />
              <Route path="/console-admin/login" element={<AdminLogin />} />
              <Route path="/console-admin/setup" element={<AdminSetup />} />
              <Route
                path="/console-admin"
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/console-admin/permissions"
                element={
                  <AdminProtectedRoute>
                    <AdminPermissionsManager />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/console-admin/guida"
                element={
                  <AdminProtectedRoute>
                    <AdminGuida />
                  </AdminProtectedRoute>
                }
              />
              <Route path="/cerca-annunci" element={<CercaAnnunci />} />
              <Route path="/annuncio/:id" element={<DettaglioAnnuncio />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/profilo" element={<Profilo />} />
              <Route path="/dashboard/preferiti" element={<Preferiti />} />
              <Route path="/modifica-annuncio/:id" element={<ModificaAnnuncio />} />
              <Route path="/test-resend" element={<TestResend />} />
              <Route path="/test-email" element={<TestEmail />} />
              <Route path="/verifica-identita" element={<VerificaIdentita />} />
              <Route path="/benvenuto" element={<SocialLanding />} />
              <Route path="/social" element={<SocialLanding />} />
              <Route path="/impegno/:invitationId" element={<ImpegnoInquilino />} />
              <Route path="/consent-form" element={<ConsentForm />} />
              <Route
                path="/console-admin/email-preview"
                element={
                  <AdminProtectedRoute>
                    <EmailPreviewDemo />
                  </AdminProtectedRoute>
                }
              />
              <Route path="/chat" element={<ChatAssistente />} />
              <Route path="/affitti-italia" element={<AffittiItalia />} />
              <Route path="/affitti-milano" element={<AffittiMilano />} />
              <Route path="/affitti-napoli" element={<AffittiNapoli />} />
              <Route path="/affitti-roma" element={<AffittiRoma />} />
              <Route path="/come-funziona" element={<ComeFunziona />} />
              <Route path="/chi-siamo" element={<ChiSiamo />} />
              <Route path="/sicurezza-verifiche" element={<SicurezzaVerifiche />} />
              <Route path="/community-studenti" element={<CommunityStudenti />} />
              <Route path="/community-studenti/onboarding" element={<StudentOnboarding />} />
              <Route path="/community-studenti/dashboard" element={<StudentDashboard />} />
              <Route path="/documenti-contratto" element={<DocumentiContratto />} />
              <Route path="/ricevi-avvisi" element={<RiceviAvvisi />} />
              <Route path="/modulo-dati" element={<ModuloDati />} />
              <Route path="/richiesta-canone-concordato" element={<RichiestaCanoneConcordato />} />
              <Route path="/canone-concordato" element={<CanoneConcordato />} />
              <Route path="/affitti-studenti-milano" element={<AffittiStudentiMilano />} />
              <Route path="/ads" element={<AdsLanding />} />
              <Route path="/mandato-unioncasa" element={<MandatoUnioncasa />} />
              <Route path="/dante" element={<DanteShortTerm />} />
              <Route path="/iscrizione-short-term" element={<IscrizioneShortTerm />} />
              <Route path="/paola" element={<Paola />} />
              <Route path="/assistente-vocale" element={<Paola />} />
              <Route path="/avatar-demo" element={<AvatarDemo />} />
              <Route path="/lip-sync-demo" element={<LipSyncDemo />} />
              <Route path="/visita-risposta" element={<VisitaRisposta />} />
              <Route path="/scelta-orario" element={<SceltaOrario />} />
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <FloatingAssistant />
            <CookieBanner />
          </PageTrackingProvider>
        </BrowserRouter>
      </AppErrorBoundary>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
